import { getAuthHeaders, getBaseUrl } from './apiClient';

export type SortBy = 'clicks' | 'impressions' | 'position' | 'ctr' | 'title' | 'published' | 'updated' | 'indexed' | 'category' | 'pillar' | 'funnel' | 'ai';
export type SortOrder = 'asc' | 'desc';

export type TopQuery = {
  query: string;
  clicks: number;
  impressions: number;
  position: number;
  ctr: number;
};

export type DailySeriesPoint = {
  date: string;
  clicks: number;
  impressions: number;
  position: number;
};

/**
 * Combined per-article row joining content-inventory + GSC metrics + indexation.
 * Returned by `list()`. The single-URL `getOne()` returns a different shape
 * (UrlMetricsSummary, kept below for compat).
 */
export type ArticlePerformanceRow = {
  url: string;
  title: string;
  category: string | null;
  pillar: string | null;
  funnel_stage: string | null;
  content_type: 'post' | 'page' | null;
  published_at: string | null;
  modified_at: string | null;
  // Provenance flags
  in_inventory: boolean;
  from_pipeline: boolean;
  matched_to_gsc: boolean;
  // Indexation
  is_indexed: boolean | null;
  coverage_state: string | null;
  indexation_checked_at: string | null;
  // 28-day aggregates
  window_days: number;
  clicks: number;
  impressions: number;
  ctr: number;
  organic_impressions?: number;
  organic_clicks?: number;
  organic_ctr?: number | null;
  ai_overview_impressions?: number;
  ai_overview_clicks?: number;
  avg_position: number | null;
  // Latest day snapshot (most recent COMPLETE day, T-2)
  latest_date: string | null;
  latest_clicks: number;
  latest_impressions: number;
  latest_position: number | null;
  // 7-day average (most recent 7 complete days)
  clicks_7d_avg: number;
  impressions_7d_avg: number;
  position_7d_avg: number | null;
  // Sparklines (per day, length ~27 = window_days minus partial trail)
  clicks_series: number[];
  impressions_series: number[];
  data_points: number;
  // GA4 metrics (from Google Analytics)
  ga4_pageviews?: number;
  ga4_sessions?: number;
  ga4_engaged_sessions?: number;
  ga4_bounce_rate?: number | null;
  ga4_conversions?: number;
  ga4_top_channel?: string | null;
  ga4_data_points?: number;
  ga4_latest_date?: string | null;
  ga4_sessions_series?: number[];
  ga4_ai_sessions?: number;
  ga4_ai_sources?: Record<string, number> | null;
  ga4_ai_sessions_series?: number[];
  // 52-week series (weekly aggregates for trend charts, ISO weeks Mon–Sun)
  weekly_clicks?: number[];
  weekly_impressions?: number[];
  weekly_sessions?: number[];
  weekly_ai_sessions?: number[];
  // 13-month series (calendar month aggregates for trend charts)
  monthly_clicks?: number[];
  monthly_impressions?: number[];
  monthly_sessions?: number[];
  monthly_ai_sessions?: number[];
};

export type ContentTypeFilter = 'post' | 'page' | 'all';

export type ListMetricsParams = {
  days?: number;
  sortBy?: SortBy;
  order?: SortOrder;
  limit?: number;
  offset?: number;
  q?: string;
  matchedOnly?: boolean;
  contentType?: ContentTypeFilter;
};

export type ListMetricsResponse = {
  window_days: number;
  total_count: number;
  limit: number;
  offset: number;
  sort_by: SortBy;
  order: SortOrder;
  metrics: ArticlePerformanceRow[];
  weekly_publications?: number[];
  monthly_publications?: number[];
  // ISO start dates for each weekly/monthly series bucket (server-computed,
  // aligned with the per-article weekly_*/monthly_* series)
  weekly_period_starts?: string[];
  monthly_period_starts?: string[];
  window_start?: string | null;
  data_through?: string | null;
};

// Single-URL detail returned by the drill-down endpoint
export type UrlDetail = {
  url: string;
  window_days: number;
  clicks: number;
  impressions: number;
  ctr: number;
  organic_impressions?: number;
  organic_clicks?: number;
  organic_ctr?: number | null;
  ai_overview_impressions?: number;
  ai_overview_clicks?: number;
  avg_position: number | null;
  data_points: number;
  first_date: string | null;
  last_date: string | null;
  series: DailySeriesPoint[];
  top_queries_by_clicks: TopQuery[];
  top_queries_by_impressions: TopQuery[];
  top_queries_date: string | null;
  // 52-week / 13-month trend series (full history, same buckets as the list
  // snapshot; last entry is the current partial period)
  weekly_clicks?: number[];
  weekly_impressions?: number[];
  weekly_sessions?: number[];
  weekly_ai_sessions?: number[];
  monthly_clicks?: number[];
  monthly_impressions?: number[];
  monthly_sessions?: number[];
  monthly_ai_sessions?: number[];
  weekly_period_starts?: string[];
  monthly_period_starts?: string[];
  indexation: {
    is_indexed: boolean;
    verdict: string | null;
    coverage_state: string | null;
    last_crawled: string | null;
    google_canonical: string | null;
    checked_at: string | null;
  } | null;
  inventory: {
    title: string | null;
    category: string | null;
    content_type: string | null;
    pillar: string | null;
    published_at: string | null;
    word_count: number | null;
    from_pipeline: boolean;
  } | null;
  links?: {
    incoming: { url: string; title: string; anchor_text: string }[];
    outgoing: { url: string; title: string; anchor_text: string }[];
  };
  // GA4 data (from Google Analytics)
  ga4?: {
    pageviews: number;
    sessions: number;
    engaged_sessions: number;
    bounce_rate: number | null;
    engagement_rate: number | null;
    conversions: number;
    total_users: number;
    engagement_duration: number;
    avg_engagement_time: number | null;
    scrolled_users: number;
    scroll_rate: number | null;
    landing_sessions: number;
    ai_sessions: number;
    ai_sources: Record<string, number> | null;
    top_channels: { channel: string; sessions: number }[];
    data_points: number;
    series: {
      date: string;
      pageviews: number;
      sessions: number;
      engaged_sessions: number;
      bounce_rate: number;
      conversions: number;
      total_users: number;
      engagement_duration: number;
      scrolled_users: number;
      landing_sessions: number;
    }[];
  };
};

export type MetricsFetchStatus = {
  status: 'never_run' | 'running' | 'completed' | 'completed_with_errors' | 'failed';
  updated_at?: string;
  phase?: 'scheduled' | 'backfill';
  current_date?: string;
  last_completed_date?: string;
  rows_written?: number;
  days_done?: number;
  days_total?: number;
  failures?: number;
  start_date?: string;
  end_date?: string;
};

export type GraphNode = {
  id: string;         // normalized URL
  title: string;
  category: string | null;
  pillar: string | null;
  content_type: string | null;
  clicks: number;
  impressions: number;
  avg_position: number | null;
  community: number | null;       // level-1 community (Leiden, 0-based by size desc)
  sub_community: number | null;   // level-2 sub-community (within each level-1)
};

export type GraphEdge = {
  source: string;     // node id
  target: string;     // node id
  anchors: string[];  // anchor texts (up to 5)
};

export type SubCommunityInfo = {
  id: number;
  size: number;
};

export type CommunityInfo = {
  id: number;
  size: number;
  sub_communities: SubCommunityInfo[];
};

export type GraphLinksResponse = {
  node_count: number;
  edge_count: number;
  orphan_count: number;
  community_count: number;
  nodes: GraphNode[];
  edges: GraphEdge[];
  orphans: GraphNode[];
  communities: CommunityInfo[];
};

// Health checks
export type HealthCheckDefinition = {
  id: string;
  name: string;
  description: string;
  weight: number;
  benchmarks?: {
    source?: string;
    note?: string;
    expected_ctr_by_position?: Record<string, string>;
    severity_scale?: Record<string, string>;
    threshold?: string;
  };
};

export type ArticleCheckResult = {
  check_id: string;
  severity: number;
  detail: string;
  data: Record<string, any>;
};

export type HealthArticle = {
  url: string;
  title: string;
  category: string | null;
  published_at: string | null;
  word_count: number;
  clicks: number;
  impressions: number;
  avg_position: number | null;
  position_7d_avg: number | null;
  ctr: number;
  clicks_series: number[];
  matched_to_gsc: boolean;
  data_quality: 'sufficient' | 'insufficient' | 'no_data';
  data_points: number;
  checks: ArticleCheckResult[];
  total_score: number;
  failing_count: number;
  diagnosis: string | null;
};

export type HealthTiers = {
  urgent: number;
  needs_attention: number;
  minor: number;
  healthy: number;
};

export type HealthReport = {
  status: string;
  generated_at?: string;
  total_articles_checked?: number;
  tiers?: HealthTiers;
  not_indexed_count?: number;
  check_definitions?: HealthCheckDefinition[];
  not_indexed?: HealthArticle[];
  articles: HealthArticle[];
};

export type HealthRefreshStatus = {
  status: 'idle' | 'running' | 'completed';
  updated_at?: string;
  step?: string;
  phase?: string;
  progress?: { done: number; total: number };
  total_checked?: number;
  articles_with_issues?: number;
};

export type IndexationRefreshResult = {
  checked: number;
  succeeded: number;
  failed: number;
  indexed: number;
  not_indexed: number;
  checked_at: string;
};

// CTA performance (registry + WP placements + GA4 cta_view/cta_click)
export type CtaArticleStats = {
  url: string;
  title: string;
  placements: number;
  views: number;
  clicks: number;
  /** Clicks since view tracking launch — same window as views, basis for CTR. */
  clicks_recent: number;
};

export type CtaPerformanceRow = {
  id: string;
  headline: string;
  audience: string;
  type: string;
  target_url: string;
  funnel_stages: string[];
  in_registry: boolean;
  articles_count: number;
  placements_count: number;
  views: number;
  clicks: number;
  clicks_recent: number;
  /** clicks_recent / views — numerator and denominator cover the same window. */
  ctr: number | null;
  articles: CtaArticleStats[];
};

export type CtaPerformanceSnapshot = {
  generated_at: string;
  tracking_since: string;
  views_since: string;
  totals: {
    ctas: number;
    articles_with_ctas: number;
    placements: number;
    views: number;
    clicks: number;
    clicks_recent: number;
    ctr: number | null;
    unattributed_clicks: number;
    unknown_placements: number;
  };
  weekly: { week: string; clicks: number; views: number }[];
  ctas: CtaPerformanceRow[];
};

export const performanceMetricsService = {
  /** List articles with combined inventory + GSC metrics. Server-side sort + paginate + search. */
  async list(params: ListMetricsParams = {}): Promise<ListMetricsResponse> {
    const qs = new URLSearchParams();
    qs.set('days', String(params.days ?? 28));
    qs.set('limit', String(params.limit ?? 25));
    qs.set('offset', String(params.offset ?? 0));
    qs.set('content_type', params.contentType ?? 'post');
    if (params.sortBy) qs.set('sort_by', params.sortBy);
    if (params.order) qs.set('order', params.order);
    if (params.q) qs.set('q', params.q);
    if (params.matchedOnly) qs.set('matched_only', 'true');

    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/metrics?${qs}`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to list metrics (${resp.status})`);
    return await resp.json();
  },

  /** Detailed metrics for one URL — daily series, top queries (by clicks + impressions), indexation. */
  async getOne(url: string, days: number = 28): Promise<UrlDetail> {
    const params = new URLSearchParams({ url, days: String(days) });
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/metrics?${params}`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to get metrics for url (${resp.status})`);
    return await resp.json();
  },

  /** CTA performance snapshot: registry + article placements + GA4 view/click events. */
  async getCtaPerformance(refresh: boolean = false): Promise<CtaPerformanceSnapshot> {
    const qs = refresh ? '?refresh=true' : '';
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/ctas${qs}`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to get CTA performance (${resp.status})`);
    return await resp.json();
  },

  /** Last fetch status (when did GSC last sync, was there a backfill running, etc.). */
  async status(): Promise<MetricsFetchStatus> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/metrics/status`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to get metrics status (${resp.status})`);
    return await resp.json();
  },

  /** Trigger a targeted indexation refresh — only checks new and not-indexed articles. */
  async refreshIndexation(): Promise<IndexationRefreshResult> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/indexation/refresh`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to refresh indexation (${resp.status})`);
    return await resp.json();
  },

  /** Get current indexation refresh status (for polling progress). */
  async getIndexationStatus(): Promise<{ status: string; step?: string; progress?: { done: number; total: number }; checked_at?: string }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/indexation/status`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to get indexation status (${resp.status})`);
    return await resp.json();
  },

  /** Get the internal-link graph: nodes = inventory articles, edges = links between them. */
  async getGraphLinks(): Promise<GraphLinksResponse> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/graph/links`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to load link graph (${resp.status})`);
    return await resp.json();
  },

  /** Get the GSC keyword overlap graph: edges = shared search queries (exact match, impressions). */
  async getGraphQueries(): Promise<GraphLinksResponse> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/graph/queries`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to load keyword graph (${resp.status})`);
    return await resp.json();
  },

  /** Get health check definitions. */
  async getHealthChecks(): Promise<{ checks: HealthCheckDefinition[] }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/health/checks`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to load health checks (${resp.status})`);
    return await resp.json();
  },

  /** Get the most recent health report. */
  async getHealthReport(): Promise<HealthReport> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/health/report`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to load health report (${resp.status})`);
    return await resp.json();
  },

  /** Poll the health refresh progress. */
  async getHealthRefreshStatus(): Promise<HealthRefreshStatus> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/health/refresh-status`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to get refresh status (${resp.status})`);
    return await resp.json();
  },

  /** Trigger a health check refresh (async, ~60s). */
  async refreshHealth(): Promise<{ status: string; message: string }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/health/refresh`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to trigger health refresh (${resp.status})`);
    return await resp.json();
  },

  /** Manually rebuild the performance snapshot from current data. ~24s. */
  async rebuildSnapshot(): Promise<{ status: string; stats: { total: number; posts: number; pages: number; orphans: number } }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/performance/metrics/rebuild`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to rebuild snapshot (${resp.status})`);
    return await resp.json();
  },
};
